import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { Space } from '../../mainApi';
import { SpacesService } from '../../services/spaces.service';
import { getSpaceUrl } from '../../stores/spacesStore';

const spaceTypeImage = {
  board: 'https://storage.googleapis.com/dotstorming-img/graphic_voting.svg',
  wall: 'https://storage.googleapis.com/dotstorming-img/graphic_wall.svg',
  collage: 'https://storage.googleapis.com/dotstorming-img/graphic_collage.svg',
  game: 'https://storage.googleapis.com/dotstorming-img/graphic_game.svg',
};

type SpaceCardProps = {
  space: Space;
  onSpaceMenuOpen: (event: React.MouseEvent<HTMLElement>, space: Space) => void;
};

export const SpaceCard = ({ space, onSpaceMenuOpen }: SpaceCardProps) => {
  const { search } = useLocation();
  const spaceTypeLabel =
    space.type === 'board'
      ? 'Board'
      : space.type === 'wall'
      ? 'Whiteboard'
      : space.type === 'game'
      ? 'Quick Sort'
      : 'Collage';
  const workspaceId = new URLSearchParams(search).get('workspaceId');
  const thumbnailUrl = useMemo(() => SpacesService.getThumbnailUrl(space), [space]);

  return (
    <Card
      sx={{
        position: 'relative',
        border: '1px solid #e7e7e7',
        borderRadius: '6px',
        boxShadow: 'none',
        overflow: 'hidden',
        '&:hover': {
          borderColor: '#d8dde7',
          boxShadow: '0 2px 8px rgba(2, 43, 58, 0.08)',
        },
      }}
    >
      {space.isOwner && (
        <IconButton
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 2,
            color: '#022b3a',
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onSpaceMenuOpen(event, space);
          }}
        >
          <MoreVertOutlinedIcon fontSize="small" />
        </IconButton>
      )}

      {space.archived && (
        <Chip
          size="small"
          label="Archived"
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            zIndex: 2,
            fontWeight: 700,
            bgcolor: '#ffffff',
          }}
        />
      )}

      <CardActionArea
        component="a"
        href={getSpaceUrl(space, workspaceId)}
        sx={{ alignItems: 'stretch', display: 'flex', flexDirection: 'column' }}
      >
        <Tooltip title={spaceTypeLabel}>
          <CardContent
            sx={{
              position: 'relative',
              height: 143,
              bgcolor: '#edeef4',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 0,
            }}
          >
            <Box
              component="img"
              src={thumbnailUrl ?? spaceTypeImage[space.type]}
              alt={spaceTypeLabel}
              sx={{
                width: thumbnailUrl ? '100%' : 110,
                height: thumbnailUrl ? '100%' : 110,
                objectFit: thumbnailUrl ? 'cover' : 'contain',
                p: 0,
                m: 0,
              }}
            />
          </CardContent>
        </Tooltip>

        <CardContent
          sx={{
            borderTop: '1px solid #e7e7e7',
            px: 1.8,
            py: 1.6,
            '&:last-child': { pb: 1.6 },
          }}
        >
          <Typography
            sx={{
              fontSize: 13.5,
              lineHeight: 1.3,
              fontWeight: 700,
              color: '#022b3a',
            }}
          >
            {(space.title as string | undefined) || 'Untitled space'}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};
